import React from 'react';
import PropTypes from 'prop-types';
import { withTheme } from 'styled-components';
import Colors from '../../theme/colors';
import Typography from '../../base/Typography';
import AssistiveText from '../../base/AssistiveText';
import { Row } from '../../base/Grid';

import {
  DivDynamicCalloutCardStyled,
  DivDynamicCalloutBannerStyled,
  ArticleDynamicCalloutStyled,
  SpanArticleFlagStyled,
  AnchorArticleContainerStyled,
  TypographyArticleFlagStyled,
  DynamicCalloutCardContainer,
  ColImageContainerStyled,
  ColContentContainerStyled,
  DivContentContainerStyled,
  DivCardImageStyled,
  TypographyCardStyled,
  TypographyVisualLinkStyled,
  IconVisualLinkStyled,
  DivBannerImageContainerStyled,
  ButtonDynamicCalloutBannerStyled,
  ButtonDynamicCalloutOverlayStyled,
} from './styles';

const buttonTextDefault = 'Get started';
const jsRedirect = (urlPath) => {
  window.location.href = (urlPath);
};

export const DynamicCalloutComponent = ({
  orientation,
  actionId,
  image,
  metadata,
  title,
  buttonText,
  url,
  tag,
  flag,
  visualLink,
  assistiveText,
}) => {
  // Adding appropriate color based on the flag prop. Primary is default.
  const flagPrimary = Colors.accent.lightBlue2;
  const flagSecondary = Colors.cta.yellow;
  // If we add more colors, we will need to update this expression
  const flagColor = (flag === 'secondary')
    ? flagSecondary
    : flagPrimary;

  // card CTA orientation
  if (orientation === 'card') {
    return (
      <DivDynamicCalloutCardStyled>
        <ArticleDynamicCalloutStyled>
          <AnchorArticleContainerStyled
            href={url}
            id={actionId}
            data-tracking-id={actionId}
            >
            <AssistiveText
              text={assistiveText}
              component='p'
            />
            <SpanArticleFlagStyled flagColor={flagColor}>
              <TypographyArticleFlagStyled
                component='p'
                color={Colors.neutral.black}
                weight={500}
                disableBottomPadding>
                {tag}
              </TypographyArticleFlagStyled>
            </SpanArticleFlagStyled>
            <DivCardImageStyled
              image={image}
              data-bg={`url(${image})`} /* If a non URL image is passed in, how should we handle setting the data-bg? */
              className="lazy"
              />
            <DynamicCalloutCardContainer className='js-dynamic-callout-card-content-container'>
              {!!metadata
              && <Typography
                className='nmx-no-print'
                component='p'
                variant='h6'
                color={Colors.neutral.gray41}
                weight={300}
                uppercase>
                {metadata}
              </Typography>}
              <TypographyCardStyled
                component='h3'
                variant='h4'
                color={Colors.neutral.gray29}
                weight={500}
                hasLink={visualLink}>
                {title}
              </TypographyCardStyled>
              {!!visualLink
              && <TypographyVisualLinkStyled
                className='nmx-no-print'
                component='p'
                color={Colors.cta.blue}
                weight={500}
                disableBottomPadding>
                {visualLink}
                <IconVisualLinkStyled />
              </TypographyVisualLinkStyled>}
            </DynamicCalloutCardContainer>
          </AnchorArticleContainerStyled>
        </ArticleDynamicCalloutStyled>
      </DivDynamicCalloutCardStyled>
    );
  }

  return (
    <DivDynamicCalloutBannerStyled>
      <Row align='center'>
        <ColImageContainerStyled xsmall={12} small={5} medium={6} large={5} nested>
          <DivBannerImageContainerStyled
            data-bg={image}
            className="lazy"
            image={image}/>
        </ColImageContainerStyled>
        <ColContentContainerStyled xsmall={12} small={7} medium={6} large={7}>
          <DivContentContainerStyled>
            <Typography
              component='h2'
              variant='h3'
              color={Colors.neutral.gray29}
              disableBottomPadding>
              {title}
            </Typography>
            <ButtonDynamicCalloutBannerStyled
              role='link'
              variant='tertiary'
              id={`${actionId}-button`}
              data-tracking-id={`${actionId}-button`}
              aria-label={assistiveText}
              onClick={() => {
                jsRedirect(url);
              }}>
              {buttonText || buttonTextDefault}
            </ButtonDynamicCalloutBannerStyled>
          </DivContentContainerStyled>
        </ColContentContainerStyled>
      </Row>
      {/* Clickable overlay for the banner variation */}
      <ButtonDynamicCalloutOverlayStyled
        role='link'
        id={actionId}
        data-tracking-id={actionId}
        aria-label={assistiveText}
        onClick={() => {
          jsRedirect(url);
        }} />
    </DivDynamicCalloutBannerStyled>
  );
};

DynamicCalloutComponent.propTypes = {
  orientation: PropTypes.string,
  actionId: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  metadata: PropTypes.string,
  title: PropTypes.string.isRequired,
  buttonText: PropTypes.string,
  url: PropTypes.string.isRequired,
  tag: PropTypes.string,
  flag: PropTypes.string,
  visualLink: PropTypes.string,
};

export default withTheme(DynamicCalloutComponent);
